import { Box, useTheme } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from "../../../../../theme";
import Shbutton from "../../../../theme/buttons/Shbutton";
import Text from "../../../../theme/text/Text";
import Dropdown from "../../../../theme/dropout/DropOut";
import ShValidatedInput from "../../../../theme/inputs/ShValidatedInput";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import * as yup from "yup";
import { yupRequired } from "../../../../../utils/validation";
import SpDesktopFooter from "../../../../theme/sidepanel2/SidepanelFooter";
import EtailizeFieldRenderer from "../../../ModalsProgression/MpAddWebshop/components/etailize/EtailizeFieldRenderer";
import EtailizeErrorBoundary from "../../../ModalsProgression/MpAddWebshop/components/etailize/EtailizeErrorBoundary";
import { validateField } from "../../../ModalsProgression/MpAddWebshop/utils/etailize/etailizeValidation";
import { logError } from "../../../ModalsProgression/MpAddWebshop/utils/etailize/etailizeErrorHandling";
import { processFieldConfigurations } from "../../../ModalsProgression/MpAddWebshop/utils/etailize/etailizeHelpers";
import { useEditWebshopCredentialsMutation, useDeleteWebshopCredentialsMutation } from "../../../../../newapi/webshop/webshopSlice";
import { showToastMessageUpdateRequest, showToastMessageRequestError, showToastMessageDeleteRequest } from "../../../../../utils/toasts";
import MConfirmDelete from "../../../Modals/MConfirmDelete";

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return props.hasModifyPermission ? 
        <ButtonGroup version={2}>
            <Shbutton
                isLoading={props.isLoading}
                className={"group-btn"}
                onClick={props.deleteAction}
                color="red"
                variant="contained"
                endIcon={<DeleteIcon />}
            >
                {t('delete')}
            </Shbutton>
            <Shbutton
                isLoading={props.isLoading}
                className={"group-btn"}
                onClick={props.action}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                endIcon={<SaveIcon />}
            >
                {t('save')}
            </Shbutton>
        </ButtonGroup> : <></>;
};

const DetailsPanel = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [open, setOpen] = useState(false);
    
    const hasModifyPermission = useSelector((state) => 
        selectUserPermission(state, [
            UserPrivileges.WEBSHOPCREDENTIALS_MANAGE, 
            UserPrivileges.SETTINGS_ALL
        ])
    );

    const [etailizeWebshopInfo, setEtailizeWebshopInfo] = useState({
        id: 0,
        description: "",
        webshop_type: "",
        state: "",
        credentials: {
            marketplace_id: "",
            auth_data: {},
            marketplace_config: null
        }
    });

    const [authData, setAuthData] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [fieldValidationStatus, setFieldValidationStatus] = useState({});
    const [isValidating, setIsValidating] = useState(false);

    const [editWebshopCredentials, { isLoading }] = useEditWebshopCredentialsMutation();
    const [deleteWebshopCredentials] = useDeleteWebshopCredentialsMutation();

    // Debounce utility function
    const useDebounce = (callback, delay) => {
        const [debounceTimer, setDebounceTimer] = useState(null);

        const debouncedCallback = useCallback((...args) => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            
            const timer = setTimeout(() => {
                callback(...args);
            }, delay);
            
            setDebounceTimer(timer);
        }, [callback, delay, debounceTimer]);

        return debouncedCallback;
    };

    // Get field definitions function
    const getFieldDefinitions = useCallback(() => {
        try {
            const marketplaceConfig = etailizeWebshopInfo?.credentials?.marketplace_config;
            if (marketplaceConfig?.fields) {
                // Convert the field definitions to the expected format
                return marketplaceConfig.fields.map(field => ({
                    id: field.id,
                    name: field.id, // Use id as name for consistency
                    label: field.label,
                    placeholder: field.placeholder,
                    tooltip: field.tooltip,
                    fieldType: field.fieldType,
                    validation: field.validation,
                    valueOptions: field.valueOptions,
                    type: field.fieldType, // Map fieldType to type for compatibility
                    required: field.validation?.isRequired || false
                }));
            }
        } catch (error) {
            console.warn('Error processing field definitions:', error);
        }
        return [];
    }, [etailizeWebshopInfo?.credentials?.marketplace_config]);

    // Debounced validation function
    const performValidation = useCallback((fieldName, value) => {
        const fields = getFieldDefinitions();
        const field = fields.find(f => f.id === fieldName);
        
        if (field) {
            // Validate the field
            const error = validateField(field, value, t);
            
            // Update validation errors
            setValidationErrors(prev => ({
                ...prev,
                [fieldName]: error || undefined
            }));
            
            // Update field validation status
            setFieldValidationStatus(prev => ({
                ...prev,
                [fieldName]: error ? 'invalid' : 'valid'
            }));
        }
    }, [t, getFieldDefinitions]);

    // Create debounced validation with 300ms delay
    const debouncedValidation = useDebounce(performValidation, 300);

    useEffect(() => {
        if (props.data) {
            setEtailizeWebshopInfo(props.data);
            
            // Initialize auth data from existing credentials
            const existingAuthData = props.data?.credentials?.auth_data || {};
            const marketplaceConfig = props.data?.credentials?.marketplace_config;
            
            if (marketplaceConfig?.fields) {
                const initialAuthData = {};
                marketplaceConfig.fields.forEach((field) => {
                    if (field && field.id) {
                        const fieldId = field.id;
                        // Use existing value if available, otherwise use empty string
                        initialAuthData[fieldId] = existingAuthData[fieldId] || '';
                    }
                });
                setAuthData(initialAuthData);
            } else {
                // Use existing auth data if no field definitions available
                setAuthData(existingAuthData);
            }
        }
    }, [props.data]);

    // Cleanup debounce timers on unmount
    useEffect(() => {
        return () => {
            // Clear any pending timers when component unmounts
            if (window.etailizeDebounceTimer) {
                clearTimeout(window.etailizeDebounceTimer);
            }
        };
    }, []);

    const handleChange = (key, value) => {
       setEtailizeWebshopInfo(prevInfo => {
            const updatedInfo = { ...prevInfo };
            const keys = key.split('.');
    
            if (keys.length === 2) {
                // Handle nested properties (e.g., 'credentials.username')
                updatedInfo[keys[0]] = {
                    ...updatedInfo[keys[0]],
                    [keys[1]]: value
                };
            } else {
                // Handle simple properties
                updatedInfo[key] = value;
            }
    
            return updatedInfo;
        });
    };

    const handleFieldChange = (fieldName, value) => {
        // Ensure value is never undefined or null
        const sanitizedValue = value === undefined || value === null ? '' : value;
        
        // Update the field value immediately
        setAuthData(prev => ({
            ...prev,
            [fieldName]: sanitizedValue
        }));

        // Update etailize webshop info with auth data in the proper nested structure
        setEtailizeWebshopInfo(prev => ({
            ...prev,
            credentials: {
                ...prev.credentials,
                auth_data: {
                    ...prev.credentials.auth_data,
                    [fieldName]: sanitizedValue
                }
            }
        }));
        
        // Set field status to pending immediately
        setFieldValidationStatus(prev => ({
            ...prev,
            [fieldName]: 'pending'
        }));
        
        // Perform debounced validation
        debouncedValidation(fieldName, sanitizedValue);
    };

    const clearFieldError = (fieldName) => {
        if (validationErrors[fieldName]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    // Validation schema
    const rules = {
        description: yupRequired('description', t)
    };
    
    const schema = yup.object().shape(rules);

    const handleSaveClick = async () => {
        try {
            if (isLoading) return;

            setFieldErrors({});
            await schema.validate(etailizeWebshopInfo, { abortEarly: false });
            
            // Prepare the data for the API call
            const updateData = {
                ...etailizeWebshopInfo,
                // Ensure credentials are properly formatted with nested auth_data
                credentials: {
                    ...etailizeWebshopInfo.credentials,
                    auth_data: {
                        ...etailizeWebshopInfo.credentials.auth_data,
                        ...authData
                    }
                }
            };
            
            const response = await editWebshopCredentials(updateData).unwrap();
            showToastMessageUpdateRequest(t, response);
        } catch (err) {
            if (err.name === 'ValidationError') {
                // Create an errors object where keys are field names and values are error messages
                const errors = err.inner.reduce((acc, currentError) => ({
                  ...acc,
                  [currentError.path]: currentError.message
                }), {});
          
                // Set the validation errors in the state
                setFieldErrors(errors);
            } else {
                // Handle other errors, such as network errors
                showToastMessageRequestError(t("update_failed"), t, err);
            }
        }
    };

    const handleDeleteClick = async () => {
        setOpen(true);
    };

    const confirmDelete = async () => {
        try {
            const response = await deleteWebshopCredentials(props.data.id).unwrap();
            showToastMessageDeleteRequest(t, response);
            props.setIsExtended(false);
        } catch (err) {
            showToastMessageDeleteRequest(t, {success: false, error: err.error});
        }
    };

    // TODO: Implement confirmation modal
    const confirmDeleteModal = {
        data: etailizeWebshopInfo?.name,
        open: open,
        setOpen: setOpen,
        confirm: confirmDelete
    };

    // Mobile actions are handled by the desktop footer in the component structure
    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions:
                <GridActions
                    hasModifyPermission={hasModifyPermission}
                    isLoading={isLoading}
                    action={handleSaveClick}
                    deleteAction={handleDeleteClick}
                />
        })
    },[hasModifyPermission,isLoading,handleSaveClick,handleDeleteClick])

    return (
        <Box height={1} display={"flex"} flexDirection={"column"} paddingX={3}
            sx={{
                "@media screen and (min-width: 48.1em)": {
                    marginBottom: 11
                }
            }}
        >
            <Dropdown title={t("general_information")} open>
                <Box flex={1}
                    sx={{
                        display: "flex",
                        gap: 4,
                        justifyContent: "flex-start",
                        marginBottom: 2
                    }}
                >
                    <Box
                        sx={{
                            "@media screen and (max-width: 47.9375em)": {
                                flex: 1
                            },
                            width: 120
                        }}
                    >
                        <Text semiBold>
                            {t('name')}
                        </Text>
                    </Box>
                    <Box flex={6}>
                        <ShValidatedInput
                            name="description"
                            value={etailizeWebshopInfo?.description ?? ""}
                            onChange={handleChange}
                            error={fieldErrors.description}
                        />
                    </Box>
                </Box>

                <Box flex={1}
                    sx={{
                        display: "flex",
                        gap: 4,
                        justifyContent: "flex-start",
                        marginBottom: 2
                    }}
                >
                    <Box
                        sx={{
                            "@media screen and (max-width: 47.9375em)": {
                                flex: 1
                            },
                            width: 120
                        }}
                    >
                        <Text semiBold>
                            {t('shipping_name')}
                        </Text>
                    </Box>
                    <Box flex={6}>
                        <ShValidatedInput
                            name="shippingName"
                            value={etailizeWebshopInfo?.shippingName ?? ""}
                            onChange={handleChange}
                        />
                    </Box>
                </Box>


            </Dropdown>

            {/* Authentication Fields */}
            <Dropdown title={t("authentication")} open>
                <EtailizeErrorBoundary
                    t={t}
                    onError={(error) => logError(error, { component: 'SpEtailizeWebshopDetails-Details' })}
                >
                    <Box sx={{ mb: 3 }}>
                        {(() => {
                            const fields = getFieldDefinitions();
                            
                            if (fields.length > 0) {
                                return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {fields.map(field => {
                                            // Ensure field value is never undefined or null
                                            const fieldValue = authData[field.id];
                                            const sanitizedValue = fieldValue === undefined || fieldValue === null ? '' : fieldValue;
                                            
                                            return (
                                                <EtailizeFieldRenderer
                                                    key={field.id}
                                                    field={field}
                                                    value={sanitizedValue}
                                                    onChange={handleFieldChange}
                                                    error={validationErrors[field.id] || undefined}
                                                    onClearError={clearFieldError}
                                                    validationStatus={fieldValidationStatus[field.id]}
                                                    disabled
                                                />
                                            );
                                        })}
                                    </Box>
                                );
                            } else {
                                return (
                                    <Box sx={{ 
                                        p: 3, 
                                        border: '1px solid #e0e0e0', 
                                        borderRadius: 2,
                                        textAlign: 'center'
                                    }}>
                                        <Text color="textSecondary">
                                            {t('no_authentication_required')}
                                        </Text>
                                    </Box>
                                );
                            }
                        })()}
                    </Box>
                </EtailizeErrorBoundary>
            </Dropdown>
                
            <SpDesktopFooter>
                <GridActions
                    hasModifyPermission={hasModifyPermission}
                    isLoading={isLoading}
                    action={handleSaveClick}
                    deleteAction={handleDeleteClick}
                />
            </SpDesktopFooter>
            
            <MConfirmDelete
                open={confirmDeleteModal.open}
                handleClose={() => confirmDeleteModal.setOpen(false)}
                data={confirmDeleteModal.data}
                confirm={confirmDeleteModal.confirm}
            />
        </Box>
    );
};

export default DetailsPanel;